import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Link2, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";

/**
 * Dialog d'auto-mapping MachineID Nayax ↔ Distributor AgroManage.
 *
 * Étape 1 : Loading initial — appelle POST /nayax/auto-map qui retourne des propositions.
 * Étape 2 : L'utilisateur valide les propositions checkbox par ligne.
 * Étape 3 : Clic "Appliquer" → POST /nayax/apply-mapping → toast + onApplied callback.
 */
export default function NayaxAutoMapDialog({ open, onOpenChange, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selected, setSelected] = useState({});  // {nayax_machine_id: bool}
  const [applying, setApplying] = useState(false);

  React.useEffect(() => {
    if (!open) {
      setProposals([]);
      setSelected({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.post("/nayax/auto-map").then((r) => {
      if (cancelled) return;
      const props = r.data.proposals || [];
      setProposals(props);
      // Sélectionne par défaut les "suggested" avec confidence ≥ 0.5
      const initSel = {};
      for (const p of props) {
        if (p.status === "suggested" && p.confidence >= 0.5) {
          initSel[p.nayax_machine_id] = true;
        }
      }
      setSelected(initSel);
      if (r.data.status === "error") {
        toast.error(r.data.message || "Erreur Nayax");
      }
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Erreur récupération des propositions");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open]);

  const toggle = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const apply = async () => {
    const mappings = proposals
      .filter((p) => selected[p.nayax_machine_id] && p.status === "suggested" && p.distributor_id)
      .map((p) => ({
        nayax_machine_id: p.nayax_machine_id,
        distributor_id: p.distributor_id,
      }));
    if (mappings.length === 0) {
      toast.info("Aucune proposition cochée à appliquer");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post("/nayax/apply-mapping", { mappings });
      toast.success(`${r.data.applied} mapping(s) appliqué(s)`);
      if (r.data.errors && r.data.errors.length > 0) {
        toast.warning(`Erreurs : ${r.data.errors.join(", ")}`);
      }
      onOpenChange(false);
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'application");
    } finally {
      setApplying(false);
    }
  };

  const suggestedCount = proposals.filter((p) => p.status === "suggested").length;
  const mappedCount = proposals.filter((p) => p.status === "already_mapped").length;
  const unmappedCount = proposals.filter((p) => p.status === "unmapped").length;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="nayax-automap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#1F4E3D]" />
            Auto-mapping des machines Nayax
          </DialogTitle>
          <DialogDescription>
            Suggestions basées sur la similarité de nom entre vos distributeurs AgroManage et les machines Nayax retournées par <code className="font-mono text-xs">GET /devices</code>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-[#575A57]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Récupération des machines Nayax…
          </div>
        ) : proposals.length === 0 ? (
          <div className="py-8 text-center text-[#575A57]">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Aucune machine retournée par Nayax. Vérifiez votre token avec « Tester la connexion ».
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <Badge className="bg-[#E8F2EC] text-[#1F4E3D] border border-[#A8C9B6]">
                {mappedCount} déjà mappés
              </Badge>
              <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border border-[#E8D3A8]">
                {suggestedCount} suggérés
              </Badge>
              <Badge className="bg-[#FBEEEA] text-[#A04E2C] border border-[#E8B0A0]">
                {unmappedCount} sans correspondance
              </Badge>
              <span className="ml-auto text-[#575A57]">
                {selectedCount} à appliquer
              </span>
            </div>
            <div className="border border-[#E5E5E0] rounded max-h-96 overflow-y-auto" data-testid="nayax-automap-table">
              {proposals.map((p) => (
                <div
                  key={p.nayax_machine_id}
                  className="flex items-center gap-3 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 text-sm hover:bg-[#F9F9F6]"
                  data-testid={`nayax-automap-row-${p.nayax_machine_id}`}
                >
                  <div className="flex-shrink-0 w-6">
                    {p.status === "suggested" && (
                      <input
                        type="checkbox"
                        checked={!!selected[p.nayax_machine_id]}
                        onChange={() => toggle(p.nayax_machine_id)}
                        className="cursor-pointer"
                        data-testid={`automap-checkbox-${p.nayax_machine_id}`}
                      />
                    )}
                    {p.status === "already_mapped" && (
                      <CheckCircle2 className="w-4 h-4 text-[#1F4E3D]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-[#1A1C1A] bg-white border border-[#E5E5E0] px-1.5 py-0.5 rounded">
                        {p.nayax_machine_id}
                      </span>
                      <span className="text-[#575A57]">{p.nayax_machine_name || "(sans nom)"}</span>
                    </div>
                    {p.distributor_name && (
                      <div className="text-xs text-[#849F90] mt-0.5">
                        ↔ {p.distributor_name}
                        {p.status === "suggested" && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider">
                            confiance : {Math.round(p.confidence * 100)}%
                          </span>
                        )}
                      </div>
                    )}
                    {p.status === "unmapped" && (
                      <div className="text-xs text-[#A04E2C] mt-0.5">
                        Aucun distributeur trouvé par similarité — créez-en un manuellement et liez-le.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="automap-cancel-btn">
            Annuler
          </Button>
          <Button
            onClick={apply}
            disabled={applying || selectedCount === 0}
            data-testid="automap-apply-btn"
          >
            {applying && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Appliquer ({selectedCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
